import { useState } from 'react'
import { toast } from 'sonner'

import { getChannelLabel } from './orders-list-filter-options'
import { MultiSelectFilter } from '@/components/orders/multi-select-filter'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { useOrderCreators } from '@/hooks/use-user-query'
import { salesOrderApi } from '@/lib/api/sales-order-api'
import {
    downloadBlob,
    filenameFromContentDisposition,
} from '@/lib/download-blob'
import { m } from '@/lib/paraglide/messages'

// 4 phương thức thanh toán cố định cho bộ lọc export (không có "Khác").
const exportPaymentMethodOptions = () => [
    { value: 'cash', label: m.payment_method_cash() },
    { value: 'bank_transfer', label: m.payment_method_bank() },
    { value: 'card', label: m.payment_method_card() },
    { value: 'cod_via_carrier', label: m.payment_method_cod_via_carrier() },
]

// Kênh bán cố định cho bộ lọc export. source_channel là free-text nên đây là
// tập kênh hệ thống dùng; nhãn tái dùng getChannelLabel, riêng "kiotviet".
const exportChannelOptions = () => [
    { value: 'kiotviet', label: 'KiotViet' },
    ...[
        'direct',
        'online',
        'pos',
        'quick_order',
        'agent',
        'phone',
        'other',
    ].map((value) => ({ value, label: getChannelLabel(value) })),
]

const filterOrAll = (all: boolean, picked: string[]) =>
    all || picked.length === 0 ? undefined : picked

/** Nút "Xuất Excel ngày" + popup chọn ngày và bộ lọc xuất (FD-396). */
export function OrdersExportDialog() {
    const [open, setOpen] = useState(false)
    // Ngày xuất Excel — mặc định hôm nay theo giờ địa phương ('en-CA' → YYYY-MM-DD).
    const [exportDate, setExportDate] = useState(() =>
        new Date().toLocaleDateString('en-CA'),
    )
    const [exporting, setExporting] = useState(false)
    // Mở popup là trống hết — không tick sẵn "Tất cả" lẫn option nào, nhân viên
    // tự tick. Để trống = không lọc (xuất tất cả).
    const [channelAll, setChannelAll] = useState(false)
    const [channelValues, setChannelValues] = useState<string[]>([])
    const [creatorAll, setCreatorAll] = useState(false)
    const [creatorIds, setCreatorIds] = useState<string[]>([])
    const [methodAll, setMethodAll] = useState(false)
    const [methodValues, setMethodValues] = useState<string[]>([])
    const openDialog = () => {
        // Mỗi lần mở là một lần chọn mới — không giữ lựa chọn lần trước.
        setChannelAll(false)
        setChannelValues([])
        setCreatorAll(false)
        setCreatorIds([])
        setMethodAll(false)
        setMethodValues([])
        setOpen(true)
    }
    // Người tạo = nhân viên đang làm + người đã nghỉ có tạo đơn (lọc đơn cũ).
    const creatorsQuery = useOrderCreators({ enabled: open })
    const creatorOptions = (creatorsQuery.data ?? []).map((u) => ({
        value: u.id,
        label: u.is_active
            ? u.full_name
            : m.order_export_creator_left({ name: u.full_name }),
    }))

    const handleExportDaily = async () => {
        if (!exportDate) {
            toast.error(m.order_export_pick_date())
            return
        }
        setExporting(true)
        try {
            const res = await salesOrderApi.exportDailyExcel(exportDate, {
                sourceChannels: filterOrAll(channelAll, channelValues),
                createdByIds: filterOrAll(creatorAll, creatorIds),
                paymentMethods: filterOrAll(methodAll, methodValues),
            })
            downloadBlob(
                res.data,
                filenameFromContentDisposition(
                    res.headers['content-disposition'] as string | undefined,
                ) ?? `bao-cao-don-hang-ngay-${exportDate}.xlsx`,
            )
            setOpen(false)
        } catch {
            toast.error(m.order_export_failed())
        } finally {
            setExporting(false)
        }
    }

    return (
        <>
            <Button size="sm" variant="outline" onClick={openDialog}>
                {m.order_export_daily_excel()}
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="sm:max-w-sm">
                    <DialogHeader>
                        <DialogTitle>
                            {m.order_export_dialog_title()}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="grid gap-2 py-2">
                        <label
                            htmlFor="order-export-date"
                            className="text-sm font-medium"
                        >
                            {m.order_export_date_label()}
                        </label>
                        <Input
                            id="order-export-date"
                            type="date"
                            value={exportDate}
                            onChange={(e) => setExportDate(e.target.value)}
                        />
                    </div>
                    <div className="grid gap-2 py-2">
                        <MultiSelectFilter
                            label={m.order_export_channel_label()}
                            options={exportChannelOptions()}
                            selectedValues={channelValues}
                            onChange={setChannelValues}
                            allSelected={channelAll}
                            onAllChange={setChannelAll}
                            emptyMeansAll
                        />
                        <MultiSelectFilter
                            label={m.order_export_creator_label()}
                            options={creatorOptions}
                            selectedValues={creatorIds}
                            onChange={setCreatorIds}
                            allSelected={creatorAll}
                            onAllChange={setCreatorAll}
                            emptyMeansAll
                        />
                        {/* Không để lỗi tải danh sách thành một ô trống im lặng. */}
                        {creatorsQuery.isLoading && (
                            <p className="text-xs text-muted-foreground">
                                {m.order_export_creators_loading()}
                            </p>
                        )}
                        {creatorsQuery.isError && (
                            <p className="flex flex-wrap items-center gap-2 text-xs text-destructive">
                                <span>
                                    {m.order_export_creators_load_failed()}
                                </span>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => void creatorsQuery.refetch()}
                                >
                                    {m.order_export_creators_retry()}
                                </Button>
                            </p>
                        )}
                        <MultiSelectFilter
                            label={m.order_export_payment_method_label()}
                            options={exportPaymentMethodOptions()}
                            selectedValues={methodValues}
                            onChange={setMethodValues}
                            allSelected={methodAll}
                            onAllChange={setMethodAll}
                            emptyMeansAll
                        />
                    </div>
                    <DialogFooter className="flex-row gap-2 pt-2 sm:justify-end">
                        <Button
                            variant="outline"
                            className="flex-1 sm:flex-none"
                            onClick={() => setOpen(false)}
                            disabled={exporting}
                        >
                            {m.common_cancel()}
                        </Button>
                        <Button
                            className="flex-1 sm:flex-none"
                            onClick={handleExportDaily}
                            disabled={exporting}
                        >
                            {m.order_export_submit()}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}
